import {getHttpClient} from '../modules/httpClient.js';
import {debounce} from '../modules/debounce.js';
import {HttpResponseError, NetworkError} from '../modules/userDefinedExceptions.js';
import {defineCustomElement} from '../utils/defineCustomElement.js';
import {t} from '../localization/translate.js';

/** Typing faster than this collapses into one filter pass, so the live status is announced once, not per keystroke. */
const SEARCH_DEBOUNCE_MS = 200;

export class VideoTranscript extends HTMLElement {
    #clickOutsideHandler = null;
    #detailsElement = null;

    /** @type {HTMLInputElement|null} */
    #searchInput = null;

    /** @type {HTMLElement|null} */
    #searchStatus = null;

    /** @type {HTMLButtonElement|null} */
    #searchClearButton = null;

    /** @type {HTMLVideoElement|null} */
    #videoElement = null;

    /** @type {HTMLInputElement|null} */
    #autoscrollCheckbox = null;

    /** @type {HTMLElement|null} The row whose section is playing */
    #activeRow = null;

    /** @type {boolean} Whether opening the details still needs to fetch the transcript. */
    #needsTranscriptFetch = true;

    /** Called each time the element is added to the document. */
    connectedCallback() {
        // Server-rendered transcripts ship their items in the HTML; only the interactions are needed
        this.#needsTranscriptFetch = !this.hasAttribute('data-server-rendered');
        this.#setupInteractions();
        this.#setupSearch();
        this.#setupSeeking();
        this.#setupActiveRowTracking();
        this.#autoscrollCheckbox = this.querySelector('[data-transcript-autoscroll]');
        this.#autoscrollCheckbox?.addEventListener('change', () => this.#scrollActiveRowIntoView());
    }

    /** Called when the element is removed from the document */
    disconnectedCallback() {
        this.#removeClickOutsideListener();
    }

    /**
     * Sets up user interactions for the transcript
     */
    #setupInteractions() {
        // Find the details element
        this.#detailsElement = this.querySelector('details');

        if (this.#detailsElement) {
            // Listen for when details opens
            this.#detailsElement.addEventListener('toggle', (event) => {
                if (event.target.open) {
                    this.#handleOpen();
                } else {
                    this.#removeClickOutsideListener();
                }
            });

            // The toggle event of a details parsed already open can fire before this listener attaches
            if (this.#detailsElement.open) {
                this.#handleOpen();
            }
        }
    }

    /** Loads the transcript on first open and wires up the dismiss interactions. */
    #handleOpen() {
        // Fetch on first open only. Listing pages embed many videos, and the transcript is
        // hidden until the reader asks for it, so loading every one upfront is wasted work.
        if (this.#needsTranscriptFetch) {
            this.#needsTranscriptFetch = false;
            this.#insertTranscript();
        }
        this.#addClickOutsideListener();
        this.#scrollActiveRowIntoView('instant');
    }

    /**
     * Adds a click listener to detect clicks outside the transcript
     */
    #addClickOutsideListener() {
        this.#removeClickOutsideListener(); // Repeated open events must not stack a second document listener

        this.#clickOutsideHandler = (event) => {
            // Check if the click was outside this element
            if (!this.contains(event.target)) {
                this.#closeTranscript();
            }
        };

        // Use capture phase to catch events before they bubble
        document.addEventListener('click', this.#clickOutsideHandler, true);

        // Also handle escape key for accessibility
        document.addEventListener('keydown', this.#handleEscapeKey);
    }

    /**
     * Removes the click outside listener
     */
    #removeClickOutsideListener() {
        if (this.#clickOutsideHandler) {
            document.removeEventListener('click', this.#clickOutsideHandler, true);
            this.#clickOutsideHandler = null;
        }
        document.removeEventListener('keydown', this.#handleEscapeKey);
    }

    /**
     * Handles escape key press: a first press clears an active search, a second one closes the transcript
     * @param {KeyboardEvent} event
     */
    #handleEscapeKey = (event) => {
        if (event.key !== 'Escape' && event.key !== 'Esc') {
            return;
        }
        if (this.#searchInput && event.target === this.#searchInput && this.#searchInput.value !== '') {
            this.#clearSearch();
            return;
        }
        this.#closeTranscript();
    };

    /** Wires the search input to filter the transcript on every keystroke, and the clear button to reset it. */
    #setupSearch() {
        this.#searchInput = this.querySelector('[data-transcript-search]');
        this.#searchStatus = this.querySelector('[data-transcript-search-status]');
        this.#searchClearButton = this.querySelector('[data-transcript-search-clear]');
        if (!this.#searchInput) {
            return;
        }
        const applySearchDebounced = debounce(() => this.#applySearch(), SEARCH_DEBOUNCE_MS);
        this.#searchInput.addEventListener('input', applySearchDebounced);
        this.#searchClearButton?.addEventListener('click', () => {
            this.#clearSearch();
            this.#searchInput?.focus();
        });
    }

    /** Sends a click anywhere on a transcript row (including the timestamp button's keyboard activation) to the player as a seek request. */
    #setupSeeking() {
        this.addEventListener('click', (event) => {
            // A click that ends a text selection is for copying, not for seeking
            if ((window.getSelection()?.toString() ?? '') !== '') {
                return;
            }
            this.#seekFromRow(event.target);
        });
    }

    /** Follows playback so the row whose section is playing carries aria-current. */
    #setupActiveRowTracking() {
        const videoId = this.getAttribute('for');
        const videoElement = typeof videoId === 'string' ? document.getElementById(videoId) : null;
        if (!(videoElement instanceof HTMLVideoElement)) {
            return; // #seekFromRow reports the missing video when the reader interacts
        }
        this.#videoElement = videoElement;
        videoElement.addEventListener('timeupdate', () => this.#markActiveRow(videoElement.currentTime));
    }

    /**
     * Marks the last row that starts at or before the given time; rows are in chronological order.
     * @param {number} currentTime Seconds
     */
    #markActiveRow(currentTime) {
        let activeRow = null;
        for (const row of this.querySelectorAll('[data-transcript-item]')) {
            const start = row.dataset.transcriptSeek;
            if (!start) {
                continue; // client-side placeholder that has not been filled yet
            }
            if (timestampToSeconds(start) > currentTime) {
                break;
            }
            activeRow = row;
        }
        this.querySelectorAll('[data-transcript-item][aria-current]').forEach((row) => {
            if (row !== activeRow) {
                row.removeAttribute('aria-current');
            }
        });
        activeRow?.setAttribute('aria-current', 'true');
        this.#markActiveCue(activeRow, currentTime);

        if (activeRow !== this.#activeRow) {
            this.#activeRow = activeRow;
            this.#scrollActiveRowIntoView();
        }
    }

    /**
     * Emphasizes the cue being spoken: the last cue in the row that starts at or before the given time.
     * @param {Element|null} activeRow
     * @param {number} currentTime Seconds
     */
    #markActiveCue(activeRow, currentTime) {
        let activeCue = null;
        for (const cue of activeRow?.querySelectorAll('[data-transcript-cue]') ?? []) {
            if (timestampToSeconds(cue.dataset.transcriptCue ?? '') > currentTime) {
                break;
            }
            activeCue = cue;
        }
        this.querySelectorAll('[data-transcript-cue-active]').forEach((cue) => {
            if (cue !== activeCue) {
                cue.removeAttribute('data-transcript-cue-active');
            }
        });
        activeCue?.setAttribute('data-transcript-cue-active', '');
    }

    /**
     * Centers the playing row in the list while Autoscroll is on. Runs only when the row changes,
     * so a reader scrolling the list is not dragged back several times a second.
     * @param {'smooth'|'instant'} behavior
     */
    #scrollActiveRowIntoView(behavior = 'smooth') {
        const row = this.#activeRow;
        if (!row || row.hidden || !this.#detailsElement?.open || !this.#autoscrollCheckbox?.checked) {
            return;
        }
        this.#centerRow(row, behavior);
    }

    /**
     * @param {HTMLElement} row
     * @param {'smooth'|'instant'} behavior
     */
    #centerRow(row, behavior) {
        const list = this.querySelector('[data-details]');
        if (!list) {
            return;
        }
        const rowTop = row.getBoundingClientRect().top - list.getBoundingClientRect().top + list.scrollTop;
        list.scrollTo({top: rowTop - (list.clientHeight - row.offsetHeight) / 2, behavior});
    }

    /**
     * @param {EventTarget|null} target An element inside the row, or the row itself
     */
    #seekFromRow(target) {
        const row = target instanceof Element ? target.closest('[data-transcript-item]') : null;
        if (!row) {
            return;
        }
        const videoId = this.getAttribute('for');
        const videoElement = typeof videoId === 'string' ? document.getElementById(videoId) : null;
        if (!(videoElement instanceof HTMLVideoElement)) {
            window.dispatchEvent(new CustomEvent('log:warning', {detail: new Error(`Transcript seek: video "${videoId}" not found`)}));
            return;
        }
        videoElement.dispatchEvent(new CustomEvent('video:seek', {detail: {time: timestampToSeconds(row.dataset.transcriptSeek ?? '')}}));

        // Picking a result ends the search; the list refills above the row, so keep the row in place
        if (this.#searchInput && this.#searchInput.value !== '') {
            this.#clearSearch();
            this.#centerRow(row, 'instant');
        }
    }

    /** Empties the query and restores the full transcript. */
    #clearSearch() {
        if (!this.#searchInput) {
            return;
        }
        this.#searchInput.value = '';
        this.#applySearch();
    }

    /**
     * Shows only the transcript items containing the current query and highlights each occurrence.
     * A blank query restores the full transcript.
     */
    #applySearch() {
        if (!this.#searchInput) {
            return;
        }
        const query = this.#searchInput.value.trim();
        const items = this.querySelectorAll('[data-transcript-item]');
        let matchCount = 0;

        items.forEach((item) => {
            let matchesQuery = false;
            item.querySelectorAll('[data-transcript-cue]').forEach((cue) => {
                matchesQuery = highlightMatches(cue, query) || matchesQuery;
            });
            item.hidden = query !== '' && !matchesQuery;
            if (matchesQuery) {
                matchCount += 1;
            }
        });

        this.#updateSearchStatus(query, matchCount);
        // Visibility rather than display keeps the button's column, so the input does not jump on the first keystroke
        this.#searchClearButton?.classList.toggle('invisible', this.#searchInput.value === '');
    }

    /**
     * @param {string} query
     * @param {number} matchCount
     */
    #updateSearchStatus(query, matchCount) {
        const noMatches = query !== '' && matchCount === 0;
        const message = noMatches ? t('No matches for ":query"', {query}) : '';
        if (this.#searchStatus) {
            this.#searchStatus.textContent = message;
        }

        const emptyMessage = this.querySelector('[data-transcript-empty]');
        if (emptyMessage) {
            emptyMessage.hidden = !noMatches;
            emptyMessage.textContent = noMatches ? message : '';
        }
        const list = this.querySelector('[data-details]');
        if (list) {
            list.hidden = noMatches;
        }
    }

    /**
     * Closes the transcript details
     */
    #closeTranscript() {
        if (!this.#detailsElement || !this.#detailsElement.open) {
            return;
        }
        // Focus inside the panel would fall to <body> once the panel hides, sending the next Tab to the page top
        if (this.contains(document.activeElement)) {
            this.#detailsElement.querySelector('summary')?.focus();
        }
        this.#detailsElement.open = false;
        this.#removeClickOutsideListener();
    }

    /**
     * Fetches transcript from backend and formats that
     * into a usable template to append to our details element
     * @returns {Promise<void>}
     */
    async #insertTranscript() {
        const detailContent = this.querySelector('[data-details]');

        /**
         * In Safari, the element is sometimes not visible, using BugSnag to investigate.
         * Update 2024-02-29: Also happens in Chrome on Windows 10, so not necessarily a browser-specific issue.
         */
        if (!detailContent) {
            const detail = new Error('Detailed list not found');
            detail.metadata = {htmlContent: {innerHTML: this.innerHTML}};
            window.dispatchEvent(new CustomEvent('log:error', {detail}));
            return;
        }

        const videoId = this.getAttribute('for');
        const videoTranscript = typeof videoId === 'string' ? document.getElementById(videoId)?.querySelector('track[type="descriptions"]') : null;
        if (!videoTranscript) {
            this.#displayStub(detailContent, t('Video Transcript not found'));
            return;
        }

        // A signed URL outlives the page render by a week; a tab restored later than that holds a
        // URL guaranteed to be rejected. Skip the doomed request and tell the reader what helps.
        const expiresAtSeconds = Number.parseInt(new URL(videoTranscript.src).searchParams.get('expires') ?? '', 10);
        if (Number.isInteger(expiresAtSeconds) && expiresAtSeconds * 1000 < Date.now()) {
            this.#reportFailure(detailContent, t('Please refresh the page to load the transcript.'), 'warning', new Error('Transcript signed URL expired before use (stale page)'));
            return;
        }

        let transcriptChunks;
        try {
            transcriptChunks = await getHttpClient().get(videoTranscript.src);
        } catch (error) {
            /**
             * Blocked (403) and throttled (429) responses come from the CDN bot protection in front
             * of the signed route, and a NetworkError means the request never left the browser —
             * none of them are defects in our code, so they are warnings. Anything else (404, 5xx)
             * is a real server-side problem and stays an error. Resetting the flag lets reopening
             * retry.
             */
            this.#needsTranscriptFetch = true;
            const isExpectedFailure = error instanceof HttpResponseError ? error.status === 403 || error.status === 429 : error instanceof NetworkError;
            this.#reportFailure(detailContent, t('Not available at the moment. Please try again later.'), isExpectedFailure ? 'warning' : 'error', error);
            return;
        }

        if (!Array.isArray(transcriptChunks)) {
            const error = new TypeError(`Expected transcript response to be a list of chunks, got ${typeof transcriptChunks}`);
            error.metadata = {
                transcript_response: {
                    value: JSON.stringify(transcriptChunks).slice(0, 200),
                    type: typeof transcriptChunks,
                    constructor: transcriptChunks?.constructor?.name,
                },
            };
            this.#needsTranscriptFetch = true;
            this.#reportFailure(detailContent, t('Not available at the moment. Please try again later.'), 'error', error);
            return;
        }

        this.#renderTranscriptItems(transcriptChunks, detailContent);
    }

    /**
     * Shows the fallback message and reports the failure to the event logger.
     * @param {HTMLOListElement} detailContent
     * @param {string} message
     * @param {'warning'|'error'} severity
     * @param {Error} error
     */
    #reportFailure(detailContent, message, severity, error) {
        this.#displayStub(detailContent, message);
        window.dispatchEvent(new CustomEvent(`log:${severity}`, {detail: error}));
    }

    /**
     * @param {HTMLOListElement} detailContent
     * @param {string} message
     */
    #displayStub(detailContent, message) {
        detailContent.textContent = '';
        const li = document.createElement('li');
        li.className = 'fg-body-md m-0 px-12 py-6 text-gray-400 md:px-16 md:py-8 lg:px-24 lg:py-12';
        li.textContent = message;
        detailContent.append(li);
    }

    /**
     * Renders the fetched chunks with the same markup the server-rendered path produces
     * @param {Array<{start: string, end: string, text: string, cues: Array<{start: string, end: string, text: string}>}>} transcriptChunks
     * @param {HTMLElement} detailContent
     */
    #renderTranscriptItems(transcriptChunks, detailContent) {
        const template = this.querySelector('template[data-detail-content]');
        // Clear placeholder
        detailContent.textContent = '';
        const withoutMilliseconds = (time) => time.split('.')[0];
        transcriptChunks.forEach((chunk) => {
            const templateClone = template.content.cloneNode(true);
            const startTime = withoutMilliseconds(chunk.start);
            const timeElement = templateClone.querySelector('[data-transcript-time]');
            if (timeElement) {
                timeElement.textContent = startTime;
                timeElement.setAttribute('aria-label', t('Play video from :time', {time: startTime}));
                timeElement.append(document.createElement('br'), withoutMilliseconds(chunk.end));
            }
            templateClone.querySelector('[data-transcript-item]')?.setAttribute('data-transcript-seek', startTime);

            const textElement = templateClone.querySelector('[data-transcript-text]');
            if (textElement) {
                chunk.cues.forEach((cue, index) => {
                    const span = document.createElement('span');
                    span.className = 'data-[transcript-cue-active]:underline data-[transcript-cue-active]:decoration-(--text-brand-default) data-[transcript-cue-active]:decoration-2 data-[transcript-cue-active]:underline-offset-4';
                    span.dataset.transcriptCue = cue.start;
                    span.textContent = cue.text;
                    if (index > 0) {
                        textElement.append(' ');
                    }
                    textElement.append(span);
                });
            }
            detailContent.append(templateClone);
        });

        // Searching is possible only once there are rows - the field stays disabled when the fetch fails
        if (this.#searchInput) {
            this.#searchInput.disabled = false;
            this.#searchInput.closest('[data-input-group]')?.querySelectorAll('[data-disabled]').forEach((wrapper) => wrapper.removeAttribute('data-disabled'));
            this.#searchInput.closest('[data-input-group]')?.removeAttribute('data-disabled');
        }
        if (this.#videoElement) {
            this.#markActiveRow(this.#videoElement.currentTime);
        }
    }
}

/**
 * Converts a WebVTT-style "HH:MM:SS" or "MM:SS" timestamp, with or without milliseconds, into seconds.
 * @param {string} timestamp
 * @returns {number}
 */
export function timestampToSeconds(timestamp) {
    return timestamp.split(':').reduce((total, part) => total * 60 + Number(part), 0);
}

/**
 * Rebuilds the element's text with every case-insensitive occurrence of the query wrapped in <mark>.
 * Reading textContent flattens any previous marks, so repeated calls never nest highlights.
 * @param {HTMLElement} textElement
 * @param {string} query
 * @returns {boolean} Whether the text contains the query
 */
export function highlightMatches(textElement, query) {
    const text = textElement.textContent ?? '';
    textElement.textContent = '';

    if (query === '') {
        textElement.textContent = text;
        return false;
    }

    // Match against the original text so a case change that alters string length cannot shift the highlight
    const pattern = new RegExp(query.replaceAll(/[$()*+.?[\\\]^{|}]/g, String.raw`\$&`), 'gi');
    let cursor = 0;
    let hasMatch = false;

    for (const match of text.matchAll(pattern)) {
        hasMatch = true;
        textElement.append(text.slice(cursor, match.index));
        const mark = document.createElement('mark');
        mark.className = 'fg-surface-brand-default fg-text-neutral-fixed-default-inverse rounded-2';
        mark.textContent = match[0];
        textElement.append(mark);
        cursor = match.index + match[0].length;
    }
    textElement.append(text.slice(cursor));

    return hasMatch;
}

defineCustomElement('ixdf-video-transcript', VideoTranscript);
